// frontend/src/services/auth.js
// FRONTEND_CODE_VERSION = "v1.0.7" // Referral: use stored ref (e.g. ref=share) on register per REFERRAL_SOURCE_PLANNING
// Purpose: Handles API calls related to user authentication and profile management.

import axios from "axios";
import { getApiBaseUrl } from "../config/api.js";
import { getStoredReferralRef } from "../utils/referralRef.js";

// Configure axios defaults for cookie handling
// This ensures all requests include credentials (cookies)
axios.defaults.withCredentials = true;

// Debug logging function
const debugLog = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`[AuthService ${timestamp}] ${message}`, data || "");
};

// Dynamic API URL from configuration
const getAPI_URL = () => getApiBaseUrl();

/**
 * DEPRECATED: Basic Auth with stored password is no longer supported for security reasons.
 * This function is kept for backward compatibility but always returns empty headers.
 * Use JWT authentication instead via getJwtHeaders().
 * @returns {Object} Always returns empty object (no password storage).
 */
const getAuthHeaders = () => {
  debugLog(
    "Basic Auth with stored password is deprecated. Use JWT authentication instead."
  );
  return {};
};

/**
 * Generates JWT Authentication headers from stored JWT token.
 * @returns {Object} An object containing the Authorization and Content-Type headers.
 * @returns {Object} An empty object if JWT token is not found in localStorage.
 */
export const getJwtHeaders = () => {
  const token = localStorage.getItem("jwtToken");
  if (token) {
    debugLog("Using stored JWT token", {
      tokenLength: token.length,
      tokenStart: token.substring(0, 20) + "...",
    });
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }
  debugLog("No JWT token found in localStorage");
  return {};
};

/**
 * Gets authentication headers using JWT only.
 * Password-based Basic Auth is no longer supported for security reasons.
 * @returns {Object} Authentication headers object with JWT token if available.
 */
const getAuthHeadersWithJwt = () => {
  // Use JWT only (no password storage)
  const jwtHeaders = getJwtHeaders();
  if (jwtHeaders.Authorization) {
    debugLog("Using JWT headers");
    return jwtHeaders;
  }
  debugLog("No JWT token available. User must log in again.");
  return {
    "Content-Type": "application/json",
  };
};

/**
 * Sends a JWT login request to the backend.
 * @param {string} email - User's email.
 * @param {string} password - User's password.
 * @returns {Promise<Object>} Response data from the backend with JWT token.
 * @throws {Error} If the login request fails.
 */
export const jwtLogin = async (email, password) => {
  debugLog("=== Starting JWT login request ===", {
    email,
    passwordLength: password.length,
    apiUrl: getAPI_URL(),
  });

  try {
    const requestData = { email, password };
    debugLog("Sending JWT login request", {
      url: `${getAPI_URL()}/api/jwt-login`,
      requestData,
    });

    const response = await axios.post(
      `${getAPI_URL()}/api/jwt-login`,
      requestData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    debugLog("JWT login response received", {
      status: response.status,
      statusText: response.statusText,
      hasData: !!response.data,
      dataKeys: response.data ? Object.keys(response.data) : [],
    });

    if (response.data.success && response.data.access_token) {
      // Store JWT token
      localStorage.setItem("jwtToken", response.data.access_token);
      // Store email for user identification (password is NOT stored for security)
      localStorage.setItem("userEmail", email);
      console.log("auth.js: JWT login successful for", email); // DEBUG

      return {
        success: true,
        message: "JWT login successful!",
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: "Invalid response from server",
      };
    }
  } catch (error) {
    debugLog("JWT login error caught", {
      status: error.response?.status,
      data: error.response?.data,
    });

    if (error.response) {
      // Log the full error response for debugging
      console.log("auth.js: JWT Login error response:", {
        status: error.response.status,
        data: error.response.data,
        hasVerificationRequired: !!error.response.data?.verification_required,
        verificationRequiredValue: error.response.data?.verification_required
      });
      
      // Handle email verification required case
      if (
        error.response.status === 401 &&
        error.response.data?.verification_required === true
      ) {
        console.log(
          "auth.js: JWT Verification required detected in response:",
          error.response.data
        ); // Additional debugging
        const result = {
          success: false,
          message: "Invalid email or password. Please try again.",
          verification_required: true,
          data: error.response.data,
        };
        console.log("auth.js: JWT returning result:", result); // Additional debugging
        return result;
      }

      console.log(
        "auth.js: JWT Login failed but no verification required:",
        error.response.data
      ); // Additional debugging
      return {
        success: false,
        message: "Invalid email or password. Please try again.",
      };
    } else if (error.request) {
      return {
        success: false,
        message:
          "Network error. Please ensure the backend is running and accessible.",
      };
    } else {
      return {
        success: false,
        message: "Invalid email or password. Please try again.",
      };
    }
  }
};

/**
 * Sends a login request to the backend (Basic Auth).
 * @param {string} email - User's email.
 * @param {string} password - User's password.
 * @returns {Promise<Object>} Response data from the backend (e.g., { message: "Login successful!" }).
 * @throws {Error} If the login request fails (network error, HTTP error, or backend-specific error).
 */
export const login = async (email, password) => {
  console.log("auth.js (v1.0.6): Attempting Basic Auth login for:", email); // DEBUG: Current file version & email
  console.log("auth.js: Using API URL:", getAPI_URL()); // DEBUG: Show current API URL
  try {
    const encodedCredentials = btoa(email + ":" + password); // Corrected string concatenation
    console.log(
      "auth.js: Sending login request with encodedCredentials (first 10 chars):",
      encodedCredentials.substring(0, 10) + "..."
    ); // DEBUG
    // Use the new /api/login endpoint for login
    const response = await axios.post(
      `${getAPI_URL()}/api/login`,
      { email, password },
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true, // Important: Include cookies for session
      }
    );
    // On successful login, store email (password is NOT stored for security)
    // Note: Basic Auth is deprecated in favor of JWT. Use jwtLogin instead.
    localStorage.setItem("userEmail", email);
    console.log("auth.js: Basic Auth login successful for", email); // DEBUG

    // Return response in the format expected by AuthContext
    return {
      success: true,
      message: response.data.message || "Login successful!",
      data: response.data,
    };
  } catch (error) {
    if (error.response) {
      console.error(
        "auth.js: Login error response status:",
        error.response.status
      ); // DEBUG
      console.error("auth.js: Login error response data:", error.response.data); // DEBUG

      // Handle email verification required case
      if (
        error.response.status === 401 &&
        error.response.data.verification_required
      ) {
        console.log(
          "auth.js: Verification required detected in response:",
          error.response.data
        ); // Additional debugging
        return {
          success: false,
          message: "Invalid email or password. Please try again.",
          verification_required: true,
          data: error.response.data,
        };
      }

      return {
        success: false,
        message: "Invalid email or password. Please try again.",
      };
    } else if (error.request) {
      console.error(
        "auth.js: Login error request (no response from server):",
        error.request
      ); // DEBUG
      return {
        success: false,
        message:
          "Network error. Please ensure the backend is running and accessible.",
      };
    } else {
      console.error("auth.js: Unexpected login error:", error); // DEBUG
      return {
        success: false,
        message: "Invalid email or password. Please try again.",
      };
    }
  }
};

/**
 * Sends a user registration request to the backend.
 * @param {string} name - User's name (optional).
 * @param {string} email - User's email.
 * @param {string} password - User's password.
 * @returns {Promise<Object>} Response data from the backend (e.g., { message: "User registered successfully!" }).
 * @throws {Error} If the registration request fails.
 */
/**
 * Checks if an email exists in the system (unified auth flow).
 * @param {string} email - User's email to check.
 * @returns {Promise<Object>} Response data from the backend.
 * @throws {Error} If the check request fails.
 */
export const checkEmail = async (email) => {
  debugLog("=== Starting email check request ===", {
    email,
    apiUrl: getAPI_URL(),
  });

  try {
    const requestData = { email };
    debugLog("Sending email check request", {
      url: `${getAPI_URL()}/api/auth/check-email`,
      requestData,
    });

    const response = await axios.post(
      `${getAPI_URL()}/api/auth/check-email`,
      requestData,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true, // Important: Include cookies for session
      }
    );

    debugLog("Email check response received", {
      status: response.status,
      statusText: response.statusText,
      hasData: !!response.data,
      dataKeys: response.data ? Object.keys(response.data) : [],
    });

    return {
      success: response.data.success || false,
      message: response.data.message || "",
      error: response.data.error || null,
      emailExists: response.data.email_exists || false, // Email existence status
      emailCheckToken: response.data.email_check_token || null, // Signed token for cookie-blocking browsers
    };
  } catch (error) {
    debugLog("Email check error caught", {
      status: error.response?.status,
      data: error.response?.data,
    });

    if (error.response) {
      // Handle rate limiting
      if (error.response.status === 429) {
        return {
          success: false,
          error:
            error.response.data?.error ||
            "Too many requests. Please try again later.",
          retryAfter: error.response.data?.retry_after || 900,
        };
      }

      // Handle 404 and 5xx errors as network/server errors
      if (error.response.status === 404 || error.response.status >= 500) {
        return {
          success: false,
          error: "Network error. Please ensure the backend is running and accessible.",
        };
      }

      return {
        success: false,
        error:
          error.response.data?.error || "Please enter a valid email address",
      };
    } else if (error.request) {
      return {
        success: false,
        error:
          "Network error. Please ensure the backend is running and accessible.",
      };
    } else {
      return {
        success: false,
        error: "An error occurred. Please try again.",
      };
    }
  }
};

export const register = async (name, email, password, language = "en", emailCheckToken = null) => {
  console.log("auth.js (v1.0.4): Attempting registration for:", email); // DEBUG: Current file version & email
  console.log("auth.js: Using API URL:", getAPI_URL()); // DEBUG: Show current API URL
  try {
    // Registration requires email/password in the JSON body.
    // referral_source: use stored ref from URL (e.g. ref=share) if present, else 'web' (direct signup).
    // Stored ref is set when user lands with ?ref=... (see utils/referralRef.js and ReferralRefPersister).
    const storedRef = getStoredReferralRef();
    const requestData = {
      name,
      email,
      password,
      language,
      referral_source: storedRef || "web",
    };
    
    // Include token if provided (for browsers blocking third-party cookies)
    if (emailCheckToken) {
      requestData.email_check_token = emailCheckToken;
      console.log("auth.js: Including email check token (cookie-blocking browser fallback)");
    }
    
    const response = await axios.post(
      `${getAPI_URL()}/api/register`,
      requestData,
      {
        withCredentials: true, // Important: Include cookies for session (if available)
      }
    );
    console.log("auth.js: Registration successful for", email); // DEBUG
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error(
        "auth.js: Register error response status:",
        error.response.status
      ); // DEBUG
      console.error(
        "auth.js: Register error response data:",
        error.response.data
      ); // DEBUG
      // Extract error message from the correct field
      const errorMessage =
        error.response.data.error ||
        error.response.data.message ||
        "Registration failed";
      // Pass session_expired flag through error
      const registrationError = new Error(errorMessage);
      if (error.response.data.session_expired) {
        registrationError.sessionExpired = true;
      }
      throw registrationError;
    } else if (error.request) {
      console.error(
        "auth.js: Register error request (no response from server):",
        error.request
      ); // DEBUG
      throw new Error(
        "Network error. Please ensure the backend is running and accessible."
      );
    } else {
      console.error("auth.js: Unexpected registration error:", error); // DEBUG
      throw new Error("An unexpected error occurred during registration.");
    }
  }
};

/**
 * Delete an unverified user account.
 * @param {string} email - User's email address.
 * @returns {Promise<Object>} Response data from the backend.
 * @throws {Error} If the deletion request fails.
 */
export const deleteUnverifiedUser = async (email) => {
  console.log("auth.js: Attempting to delete unverified user:", email);
  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/delete-unverified-user`,
      {
        email,
      }
    );
    console.log("auth.js: Delete unverified user successful for", email);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error(
        "auth.js: Delete unverified user error response:",
        error.response.data
      );
      const errorMessage =
        error.response.data.error ||
        error.response.data.message ||
        "Failed to delete account";
      throw new Error(errorMessage);
    } else if (error.request) {
      console.error(
        "auth.js: Delete unverified user error request:",
        error.request
      );
      throw new Error(
        "Network error. Please ensure the backend is running and accessible."
      );
    } else {
      console.error("auth.js: Unexpected delete unverified user error:", error);
      throw new Error("An unexpected error occurred during account deletion.");
    }
  }
};

/**
 * Clears user authentication data from local storage.
 */
export const logout = () => {
  console.log("auth.js: Logging out user and clearing localStorage."); // DEBUG
  localStorage.removeItem("userEmail");
  localStorage.removeItem("userPassword"); // Remove if it exists (legacy cleanup)
  localStorage.removeItem("jwtToken"); // Clear JWT token
};

/**
 * Fetches the current user's profile from the backend.
 * Requires authentication headers.
 * @returns {Promise<Object>} User profile data.
 * @throws {Error} If request fails or user is unauthorized.
 */
export const getProfile = async () => {
  console.log("auth.js: Attempting to get user profile."); // DEBUG
  console.log("auth.js: Using API URL:", getAPI_URL()); // DEBUG: Show current API URL
  try {
    const response = await axios.get(`${getAPI_URL()}/user/profile`, {
      headers: getAuthHeadersWithJwt(), // Use JWT-aware headers
    });
    console.log("auth.js: Profile fetched successfully."); // DEBUG
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      console.warn(
        "auth.js: Profile fetch failed: Unauthorized (401). Auto-logging out."
      ); // DEBUG
      logout();
      throw new Error("Session expired or unauthorized. Please log in again.");
    }
    console.error("auth.js: Profile fetch error:", error); // DEBUG
    throw error;
  }
};

/**
 * Updates the tracking status of a specific item.
 * @param {number} trackingId - The ID of the tracking record.
 * @param {string} newStatus - The new tracking status (e.g., 'Paused', 'Sold Out').
 * @returns {Promise<Object>} Response data from the backend.
 * @throws {Error} If update fails.
 */
export const updateItemTrackingStatus = async (trackingId, newStatus) => {
  console.log(
    `auth.js: Attempting to update tracking ${trackingId} to ${newStatus}.`
  ); // DEBUG
  console.log("auth.js: Using API URL:", getAPI_URL()); // DEBUG: Show current API URL
  try {
    const response = await axios.put(
      `${getAPI_URL()}/track/item/${trackingId}`,
      { tracking_status: newStatus },
      { headers: getAuthHeadersWithJwt() } // Use JWT-aware headers
    );
    console.log("auth.js: Tracking status updated successfully."); // DEBUG
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      console.warn(
        "auth.js: Item tracking update failed: Unauthorized (401). Auto-logging out."
      ); // DEBUG
      logout();
      throw new Error("Session expired or unauthorized. Please log in again.");
    }
    console.error(`auth.js: Error updating tracking ${trackingId}:`, error); // DEBUG
    throw error;
  }
};
